import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useFormik } from 'formik'
import * as yup from 'yup'

import Spacer from 'components/Spacer'
import Footer from 'components/Footer'
import H3 from 'components/H3'
import TextField from 'components/TextField'
import Button from 'components/Button'
import CheckboxField from 'components/CheckboxField'
import Label from 'components/Label'
import { setData } from 'store/reducers/info'

import Container from './styles'
import DbSection from './DbSection'
import HourSection from './HourSection'

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  companyName: '',
  address: '',
  suiteNumber: '',
  poBox: '',
  agreed: false,
}

export default function Summary() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const info = useSelector((state) => state.info)
  const history = useHistory()
  const [agreed, setAgreed] = useState(false)
  const [initialData, setInitialData] = useState(initialValues)

  useEffect(() => {
    if (!info.type || info.type === '') {
      history.replace('/')
    }
  }, [history, info.type])

  useEffect(() => {
    setInitialData({ ...initialValues, ...info })
    setAgreed(info.agreed)
  }, [info])

  const validationSchema = yup.object({
    firstName: yup.string().required(t('summary.form.validation.required')),
    lastName: yup.string().required(t('summary.form.validation.required')),
    email: yup
      .string()
      .email(t('summary.form.validation.invalid_email'))
      .required(t('summary.form.validation.required')),
    phoneNumber: yup
      .string()
      .length(14, t('summary.form.validation.invalid_phone'))
      .required(t('summary.form.validation.required')),
    companyName: yup.string().required(t('summary.form.validation.required')),
    address: yup.string().required(t('summary.form.validation.required')),
    agreed: yup.boolean().oneOf([true], t('summary.form.validation.required')),
  })

  const formik = useFormik({
    initialValues: initialData,
    onSubmit: (values) => {
      handleSubmit(values)
    },
    validationSchema,
    enableReinitialize: true,
  })

  const handleSubmit = (values) => {
    const data = {
      ...info,
      ...values,
    }
    dispatch(setData(data))
    history.push('/checkout')
  }

  function handleToggleViewed(value) {
    formik.setFieldValue('agreed', !value)
    setAgreed(!value)
  }

  function handleTerms() {
    const data = {
      ...info,
      ...formik.values,
    }
    dispatch(setData(data))
    history.push('/terms')
  }

  function handlePolicy() {
    const data = {
      ...info,
      ...formik.values,
    }
    dispatch(setData(data))
    history.push('/policy')
  }

  return (
    <Container>
      <Spacer height={30} />
      <h1>{t('summary.title')}</h1>
      <Spacer height={50} />

      <div className="content">
        <div className="content-item">
          {info.type === 'databases' && <DbSection info={info} />}
          {info.type === 'hours' && <HourSection info={info} />}
          <Spacer height={50} />
          <Footer />
        </div>
        <div className="content-item">
          <form onSubmit={formik.handleSubmit} className="form">
            <H3>{t('summary.contact_information')}</H3>
            <Spacer height={20} />
            <TextField
              config={{
                name: 'firstName',
                labelwidth: t('summary.form.label_width'),
                label: t('summary.form.first_name'),
                inputwidth: 310,
              }}
              formik={formik}
            />
            <TextField
              config={{
                name: 'lastName',
                labelwidth: t('summary.form.label_width'),
                label: t('summary.form.last_name'),
                inputwidth: 310,
              }}
              formik={formik}
            />
            <TextField
              config={{
                name: 'email',
                labelwidth: t('summary.form.label_width'),
                label: t('summary.form.email'),
                inputwidth: 310,
              }}
              formik={formik}
            />
            <TextField
              config={{
                name: 'phoneNumber',
                labelwidth: t('summary.form.label_width'),
                label: t('summary.form.phone_number'),
                inputwidth: 310,
                mask: 'phoneUS',
              }}
              formik={formik}
            />

            <H3>{t('summary.company_information')}</H3>
            <Spacer height={20} />
            <TextField
              config={{
                name: 'companyName',
                labelwidth: t('summary.form.label_width'),
                label: t('summary.form.company_name'),
                inputwidth: 310,
              }}
              formik={formik}
            />
            <TextField
              config={{
                name: 'address',
                labelwidth: t('summary.form.label_width'),
                label: t('summary.form.address'),
                inputwidth: 310,
              }}
              formik={formik}
            />
            <TextField
              config={{
                name: 'suiteNumber',
                labelwidth: t('summary.form.label_width'),
                label: t('summary.form.suite_number'),
                inputwidth: 310,
              }}
              formik={formik}
            />
            <TextField
              config={{
                name: 'poBox',
                labelwidth: t('summary.form.label_width'),
                label: t('summary.form.po_box'),
                inputwidth: 310,
                mask: 'number',
              }}
              formik={formik}
            />

            <Spacer height={20} />
            <div className="agree">
              <CheckboxField
                value={agreed}
                checked={agreed}
                label={t('summary.form.terms')}
                onChange={handleToggleViewed}
              />
              <Spacer width={5} />
              <button
                type="button"
                className="text_button"
                onClick={handleTerms}
              >
                {t('summary.form.terms_link')}
              </button>
              <Spacer width={5} />
              {t('summary.form.and')}
              <Spacer width={5} />
              <button
                type="button"
                className="text_button"
                onClick={handlePolicy}
              >
                {t('summary.form.policy_link')}
              </button>
            </div>
            <Label error={formik.errors.agreed} />

            <Spacer height={30} />
            <Button
              type="submit"
              className="primary"
              label={t('button.checkout')}
            />
          </form>
        </div>
      </div>
    </Container>
  )
}
